import { Injectable } from '@angular/core';
import { Mesh, PointerDragBehavior, Vector3 } from 'babylonjs';
import { Axis, RoiSizeLimits, Sides } from './roi-geometry.service';

export interface DragSideConfig {
  sideIndex: Sides;
  counterIndex: Sides;
  axis: Axis;
  dragAxis: Vector3;
  direction: 'forward' | 'backward';
}

const DRAG_SIDE_CONFIGS: DragSideConfig[] = [
  {
    sideIndex: Sides.xMin,
    counterIndex: Sides.xMax,
    axis: Axis.x,
    dragAxis: new Vector3(1, 0, 0),
    direction: 'backward',
  },
  {
    sideIndex: Sides.zMax,
    counterIndex: Sides.zMin,
    axis: Axis.z,
    dragAxis: new Vector3(0, 0, 1),
    direction: 'forward',
  },
  {
    sideIndex: Sides.xMax,
    counterIndex: Sides.xMin,
    axis: Axis.x,
    dragAxis: new Vector3(1, 0, 0),
    direction: 'forward',
  },
  {
    sideIndex: Sides.yMax,
    counterIndex: Sides.yMin,
    axis: Axis.y,
    dragAxis: new Vector3(0, 1, 0),
    direction: 'forward',
  },
  {
    sideIndex: Sides.yMin,
    counterIndex: Sides.yMax,
    axis: Axis.y,
    dragAxis: new Vector3(0, 1, 0),
    direction: 'backward',
  },
  {
    sideIndex: Sides.zMin,
    counterIndex: Sides.zMax,
    axis: Axis.z,
    dragAxis: new Vector3(0, 0, 1),
    direction: 'backward',
  },
];

@Injectable({
  providedIn: 'root',
})
export class RoiDragConfigService {
  getDragSideConfigs(): DragSideConfig[] {
    return DRAG_SIDE_CONFIGS;
  }

  createDragBehavior(dragAxis: Vector3): PointerDragBehavior {
    const behavior = new PointerDragBehavior({ dragAxis: dragAxis.clone() });
    behavior.useObjectOrientationForDragging = false;
    return behavior;
  }

  createValidateDragFn(
    sides: Mesh[],
    config: DragSideConfig,
    minSize: number,
    maxSize: number,
    isShiftPressed: () => boolean,
  ): (targetPosition: Vector3) => boolean {
    return config.direction === 'forward'
      ? this.createForwardValidator(sides, config, minSize, maxSize, isShiftPressed)
      : this.createBackwardValidator(sides, config, minSize, maxSize, isShiftPressed);
  }

  private createForwardValidator(
    sides: Mesh[],
    config: DragSideConfig,
    minSize: number,
    maxSize: number,
    isShiftPressed: () => boolean,
  ): (targetPosition: Vector3) => boolean {
    const { sideIndex, counterIndex, axis } = config;
    return (targetPosition: Vector3): boolean => {
      const tooClose = targetPosition[axis] < sides[counterIndex].position[axis] + minSize;
      if (isShiftPressed()) {
        return !tooClose;
      }
      const exceedsMax =
        maxSize < sides[sideIndex].position[axis] - sides[counterIndex].position[axis];
      return !(tooClose || exceedsMax);
    };
  }

  private createBackwardValidator(
    sides: Mesh[],
    config: DragSideConfig,
    minSize: number,
    maxSize: number,
    isShiftPressed: () => boolean,
  ): (targetPosition: Vector3) => boolean {
    const { sideIndex, counterIndex, axis } = config;
    return (targetPosition: Vector3): boolean => {
      const tooClose = targetPosition[axis] > sides[counterIndex].position[axis] - minSize;
      if (isShiftPressed()) {
        return !tooClose;
      }
      const exceedsMax =
        maxSize < sides[counterIndex].position[axis] - sides[sideIndex].position[axis];
      return !(tooClose || exceedsMax);
    };
  }

  getMaxSizeForAxis(axis: Axis, limits: RoiSizeLimits): number {
    switch (axis) {
      case Axis.x:
        return limits.maxSizeX;
      case Axis.y:
        return limits.maxSizeY;
      case Axis.z:
        return limits.maxSizeZ;
    }
  }

  buildAllDragBehaviors(
    sides: Mesh[],
    limits: RoiSizeLimits,
    isShiftPressed: () => boolean,
  ): { config: DragSideConfig; behavior: PointerDragBehavior }[] {
    return DRAG_SIDE_CONFIGS.map((config) => {
      const behavior = this.createDragBehavior(config.dragAxis);
      const maxSize = this.getMaxSizeForAxis(config.axis, limits);
      behavior.validateDrag = this.createValidateDragFn(
        sides,
        config,
        limits.minSize,
        maxSize,
        isShiftPressed,
      );
      return { config, behavior };
    });
  }
}
